/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Scatter } from 'react-chartjs-2';
import moment from 'moment';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  Filler,
  CategoryScale
} from 'chart.js';
import { useData } from '../context/data.context';
import { GraphPointStyles, ParametersType, TimeSpan } from '../constants/telosair-interfaces';
import { COLORS, GRAPH_COLORS, POINT_STYLES } from '../constants/global-constants';
import { getIconByPointStyle } from '../_helpers/graph';
import useDimensions from '../hooks/useDimension';
import Loader from './loader';

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  CategoryScale,
  Tooltip,
  Title,
  Filler,
  Legend
);

interface Props {
  data: {
    linearFitData: any;
  };
}

interface DataSet {
  label: string;
  data: Array<{ x: number; y: number }>;
  backgroundColor: string;
  yAxisID: string;
}

export default function CalibrationScatterChart({ data }: Props): ReactElement {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const dimension = useDimensions(chartContainerRef);
  const [chartData, setChartData] = useState<{ datasets: DataSet[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<any>({});

  useEffect(() => {
    const defaultOptions = {
      responsive: true,
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
        includeInvisible: false
      },
      hover: {
        mode: 'nearest',
        intersect: false,
        axis: 'x',
        includeInvisible: false
      },
      plugins: {
        legend: {
          display: false,
          position: 'top' as const
        },
        title: {
          display: false
        },
        tooltip: {
          callbacks: {
            label(context: { dataset: { label: string }; parsed: { x: number; y: number } }) {
              return `${context.dataset.label}: (${context.parsed.x.toFixed(
                2
              )}, ${context.parsed.y.toFixed(2)})`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            display: true,
            color: GRAPH_COLORS.level
          },
          title: {
            display: true,
            text: 'Reference Sensor Values',
            color: GRAPH_COLORS.level
          },
          grid: {
            display: true,
            color: GRAPH_COLORS.grid
          }
        },
        y: {
          ticks: {
            display: true,
            color: GRAPH_COLORS.level
          },
          title: {
            display: true,
            text: 'Sensor Value',
            color: GRAPH_COLORS.level
          },
          grid: {
            display: true,
            color: GRAPH_COLORS.grid
          }
        }
      },
      maintainAspectRatio: false
    };

    setOptions(defaultOptions);
  }, []);

  useEffect(() => {
    if (!data?.linearFitData) return;

    const { linearFitData } = data;
    const { ref_sensor: refSensor, slopeAndIntercept } = linearFitData;

    // Guard against undefined/null values
    if (!refSensor || !slopeAndIntercept) {
      setChartData({ datasets: [] });
      return;
    }

    const datasets: DataSet[] = [];
    let colorsUsed = 0;
    // Process each sensor's data
    slopeAndIntercept.forEach((sensor: any, index: number) => {
      const deviceId = sensor.device;
      const linearFitKey = `linear_fit_${deviceId}`;
      const correctedFitKey = `corrected_fit_${deviceId}`;

      // Add linear fit dataset
      const linearFitDataSet = linearFitData[linearFitKey] as number[];

      if (linearFitDataSet && Array.isArray(linearFitDataSet)) {
        datasets.push({
          label: `Linear Fit - Sensor ${deviceId}`,
          data: refSensor.map((x: number, i: number) => ({
            x,
            y: linearFitDataSet[i]
          })),
          backgroundColor: COLORS[colorsUsed % COLORS.length],
          yAxisID: 'y'
        });
        // eslint-disable-next-line no-plusplus
        colorsUsed++;
      }

      // Add corrected fit dataset
      const correctedFitData = linearFitData[correctedFitKey] as number[];
      if (correctedFitData && Array.isArray(correctedFitData)) {
        datasets.push({
          label: `Corrected Fit - Sensor ${deviceId}`,
          data: refSensor.map((x: number, i: number) => ({
            x,
            y: correctedFitData[i]
          })),
          backgroundColor: COLORS[colorsUsed % COLORS.length],
          yAxisID: 'y'
        });
        // eslint-disable-next-line no-plusplus
        colorsUsed++;
      }
    });

    setChartData({ datasets });
  }, [data]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (dimension.height) {
      setLoading(true);
      timeout = setTimeout(() => {
        setLoading(false);
      }, 500);
    }
    return () => clearTimeout(timeout);
  }, [dimension]);

  return (
    <div ref={chartContainerRef} className="flex flex-col w-full justify-between h-full">
      <div className="flex flex-1 items-center justify-center w-full mt-4 relative">
        {loading && <Loader />}
        {!loading && chartData && (
          <div className="flex flex-1 items-end h-full">
            <Scatter data={chartData} options={options} />
          </div>
        )}
      </div>
    </div>
  );
}
